import React from 'react';

import AuthError from './AuthError';
import AuthLoader from './AuthLoader';
import UnauthenticatedMessage from './UnauthenticatedMessage';
import { useAuth } from '../../contexts/AuthContext';
import type { WrapperComponentProps } from '../../types/common/component.types';

/**
 * Authentication wrapper component that handles all authentication states
 *
 * This component handles:
 * - Loading: Shows spinner while checking authentication
 * - Error: Shows error message with recovery options when auth fails
 * - Unauthenticated: Shows sign-in message for non-authenticated users
 * - Authenticated: Renders children for successfully authenticated users
 */
const AuthWrapper: React.FC<WrapperComponentProps> = ({ children }) => {
  const { isAuthenticated, isLoading, error } = useAuth();

  // Show loading spinner while authentication is in progress
  if (isLoading) {
    return <AuthLoader />;
  }

  // Show error component if authentication failed
  if (error) {
    return <AuthError />;
  }

  // Show sign-in message if user is not authenticated
  if (!isAuthenticated) {
    return <UnauthenticatedMessage />;
  }

  // Render authenticated content
  return <>{children}</>;
};

export default AuthWrapper;
